import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiErrorMessage, inputCls, useHierarchy } from "./hierarchy";

const TYPE_OPTIONS = [
  { value: "porta_isca", label: "Porta-isca" },
  { value: "armadilha_mecanica", label: "Armadilha mecânica" },
  { value: "armadilha_luminosa", label: "Armadilha luminosa" },
  { value: "armadilha_biologica", label: "Armadilha biológica" },
  { value: "placa_cola", label: "Placa de cola" },
  { value: "trex", label: "T-REX" },
  { value: "catrex", label: "Catrex" },
  { value: "outro", label: "Outro" },
];

const PERIODICITY_OPTIONS = [
  { value: "", label: "Não definida" },
  { value: "diaria", label: "Diária" },
  { value: "semanal", label: "Semanal" },
  { value: "quinzenal", label: "Quinzenal" },
  { value: "mensal", label: "Mensal" },
];

export default function DeviceForm({ device, defaultClientId, onClose, onSave }) {
  const [form, setForm] = useState({
    code: device?.code || "",
    type_key: device?.type_key || "porta_isca",
    client_id: device?.client_id || defaultClientId || "",
    unit_id: device?.unit_id || "",
    area_id: device?.area_id || "",
    subarea_id: device?.subarea_id || "",
    location: device?.location || "",
    description: device?.description || "",
    position: device?.position || "",
    notes: device?.notes || "",
    product: device?.product || "",
    periodicity: device?.periodicity || "",
    responsible_id: device?.responsible_id || "",
    qr_code: device?.qr_code || "",
    installed_at: device?.installed_at || "",
    latitude: device?.latitude ?? "",
    longitude: device?.longitude ?? "",
    active: device ? device.active !== false : true,
  });
  const [saving, setSaving] = useState(false);
  const { clients, units, areas, subareas } = useHierarchy({
    clientId: form.client_id,
    unitId: form.unit_id,
    areaId: form.area_id,
  });

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.value });
  const setCheck = (k) => (e) =>
    setForm({ ...form, [k]: e.target.checked });
  // Troca de nível superior limpa os níveis inferiores (nunca mistura).
  const setLevel = (k) => (e) => {
    const value = e.target.value;
    const reset = {
      client_id: { unit_id: "", area_id: "", subarea_id: "" },
      unit_id: { area_id: "", subarea_id: "" },
      area_id: { subarea_id: "" },
    }[k];
    setForm({ ...form, [k]: value, ...reset });
  };

  const missing = [
    !form.client_id && "Cliente",
    !form.unit_id && "Unidade",
    !form.area_id && "Área",
    !form.type_key && "Tipo",
    !String(form.code).trim() && "Código",
  ].filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (missing.length) {
      toast.error(`Preencha: ${missing.join(", ")}.`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        latitude:
          form.latitude === "" ? null : Number(form.latitude),
        longitude:
          form.longitude === "" ? null : Number(form.longitude),
        installed_at: form.installed_at || null,
        qr_code: form.qr_code || undefined,
        responsible_id: form.responsible_id || null,
        subarea_id: form.subarea_id || null,
      };
      if (device) {
        await api.put(`/device-monitoring/devices/v2/${device.id}`, payload);
        toast.success(`Dispositivo ${form.code} atualizado.`);
      } else {
        await api.post("/device-monitoring/devices/v2", payload);
        toast.success(`Dispositivo ${form.code} cadastrado.`);
      }
      onSave();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro ao salvar o dispositivo."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">
          {device ? "Editar Dispositivo" : "Novo Dispositivo"}
        </h3>
        {form.qr_code && (
          <span className="text-xs text-muted-foreground">
            QR: <span className="font-mono">{form.qr_code}</span>
          </span>
        )}
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm mb-1">Cliente *</label>
          <select
            data-testid="device-form-client"
            value={form.client_id}
            onChange={setLevel("client_id")}
            className={inputCls}
          >
            <option value="">Selecione o cliente…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1">Unidade *</label>
          <select
            data-testid="device-form-unit"
            value={form.unit_id}
            onChange={setLevel("unit_id")}
            disabled={!form.client_id}
            className={inputCls}
          >
            <option value="">
              {form.client_id
                ? units.length
                  ? "Selecione a unidade…"
                  : "Cliente sem unidades cadastradas"
                : "Selecione o cliente primeiro"}
            </option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1">Área *</label>
          <select
            data-testid="device-form-area"
            value={form.area_id}
            onChange={setLevel("area_id")}
            disabled={!form.unit_id}
            className={inputCls}
          >
            <option value="">
              {form.unit_id
                ? areas.length
                  ? "Selecione a área…"
                  : "Unidade sem áreas cadastradas"
                : "Selecione a unidade primeiro"}
            </option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1">Subárea</label>
          <select
            data-testid="device-form-subarea"
            value={form.subarea_id}
            onChange={set("subarea_id")}
            disabled={!form.area_id || subareas.length === 0}
            className={inputCls}
          >
            <option value="">
              {subareas.length ? "Nenhuma (opcional)" : "Sem subáreas"}
            </option>
            {subareas.map((sa) => (
              <option key={sa.id} value={sa.id}>
                {sa.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1">Código *</label>
          <input
            required
            value={form.code}
            onChange={set("code")}
            data-testid="device-form-code"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Tipo *</label>
          <select
            required
            value={form.type_key}
            onChange={set("type_key")}
            className={inputCls}
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1">Produto</label>
          <input
            value={form.product}
            onChange={set("product")}
            placeholder="isca, cola, luz…"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Local instalado</label>
          <input
            value={form.location}
            onChange={set("location")}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Posição</label>
          <input
            value={form.position}
            onChange={set("position")}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm mb-1">
            Periodicidade de monitoramento
          </label>
          <select
            value={form.periodicity}
            onChange={set("periodicity")}
            className={inputCls}
          >
            {PERIODICITY_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1">
            ID do responsável
          </label>
          <input
            value={form.responsible_id}
            onChange={set("responsible_id")}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Data de instalação</label>
          <input
            type="date"
            value={form.installed_at}
            onChange={set("installed_at")}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Latitude</label>
          <input
            type="number"
            step="any"
            value={form.latitude}
            onChange={set("latitude")}
            placeholder="-23.5505"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Longitude</label>
          <input
            type="number"
            step="any"
            value={form.longitude}
            onChange={set("longitude")}
            placeholder="-46.6333"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm mb-1">
            QR Code (gerado se vazio)
          </label>
          <input
            value={form.qr_code}
            onChange={set("qr_code")}
            placeholder="DM-…"
            className={inputCls}
          />
        </div>
        <div className="md:col-span-2 lg:col-span-3">
          <label className="block text-sm mb-1">Descrição</label>
          <textarea
            rows={2}
            value={form.description}
            onChange={set("description")}
            className={inputCls}
          />
        </div>
        <div className="md:col-span-2 lg:col-span-2">
          <label className="block text-sm mb-1">Observações</label>
          <textarea
            rows={2}
            value={form.notes}
            onChange={set("notes")}
            className={inputCls}
          />
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={setCheck("active")}
              className="h-4 w-4"
            />
            Ativo
          </label>
        </div>
        <div className="md:col-span-2 lg:col-span-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-border text-sm"
          >
            Cancelar
          </button>
          {missing.length > 0 && (
            <span className="self-center text-xs text-muted-foreground">
              Obrigatório: {missing.join(", ")}
            </span>
          )}
          <button
            type="submit"
            data-testid="device-form-save"
            disabled={saving || missing.length > 0}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
          >
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </form>
    </div>
  );
}
